import React, { useState } from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, CheckSquare, X } from 'lucide-react';
import TimeSlotGrid from '@/components/schedule/TimeSlotGrid';
import TimeSlotCreateForm from '@/components/schedule/TimeSlotCreateForm';
import TimeSlotEditForm from '@/components/schedule/TimeSlotEditForm';
import TimeSlotDeleteDialog from '@/components/schedule/TimeSlotDeleteDialog';
import TimeSlotMassDeleteDialog from '@/components/schedule/TimeSlotMassDeleteDialog';
import ScheduleFilters from '@/components/schedule/ScheduleFilters';
import { TimeSlot, DayOfWeek } from '@/models/Schedule';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useTimeSlots } from '@/hooks/useTimeSlots';
import { useWorkoutTypes } from '@/hooks/useWorkoutTypes';
import { useScheduleFilters } from '@/hooks/useScheduleFilters';
import { useRolePermissions } from '@/hooks/useRolePermissions';
import { toast } from '@/components/ui/sonner';

const SchedulePage = () => {
  const { t } = useTranslation();
  const { canManageSchedule } = useRolePermissions();
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [editingTimeSlot, setEditingTimeSlot] = useState<TimeSlot | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<TimeSlot | null>(null);
  const [preselectedDay, setPreselectedDay] = useState<DayOfWeek | undefined>(undefined);
  
  // Mass deletion state
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState<Set<string>>(new Set());
  const [showMassDeleteDialog, setShowMassDeleteDialog] = useState(false);

  const {
    timeSlots,
    isLoading,
    createMultipleTimeSlots,
    updateTimeSlot,
    deleteTimeSlot,
  } = useTimeSlots();

  const { workoutTypes } = useWorkoutTypes();

  const {
    selectedDay,
    selectedWorkoutType,
    filteredTimeSlots,
    setSelectedDay,
    setSelectedWorkoutType,
    clearFilters,
  } = useScheduleFilters({ timeSlots });

  const hasActiveFilters = selectedDay !== 'all' || selectedWorkoutType !== 'all';

  const handleCreateTimeSlots = async (
    newTimeSlots: {
      day_of_week: string;
      start_time: string;
      end_time: string;
      workout_type_id: string;
      max_clients: number;
      is_active: boolean;
    }[]
  ) => {
    try {
      await createMultipleTimeSlots(newTimeSlots);
    } catch (error) {
      console.error('Error creating time slots:', error);
    }
  };

  const handleCreateTimeSlotForDay = (day: DayOfWeek) => {
    setPreselectedDay(day);
    setIsCreateFormOpen(true);
  };

  const handleEditTimeSlot = (timeSlot: TimeSlot) => {
    setEditingTimeSlot(timeSlot);
    setIsEditFormOpen(true);
  };

  const handleUpdateTimeSlot = async (
    id: string,
    data: {
      start_time: string;
      end_time: string;
      workout_type_id: string;
      max_clients: number;
    }
  ) => {
    await updateTimeSlot({ id, ...data });
    setEditingTimeSlot(null);
  };

  const handleDeleteTimeSlot = (timeSlot: TimeSlot) => {
    setDeleteDialog(timeSlot);
  };

  const confirmDeleteTimeSlot = async () => {
    if (deleteDialog) {
      try {
        await deleteTimeSlot(deleteDialog.id);
        setDeleteDialog(null);
      } catch (error) {
        console.error('Error deleting time slot:', error);
      }
    }
  };

  const handleCreateFormClose = (open: boolean) => {
    setIsCreateFormOpen(open);
    if (!open) {
      setPreselectedDay(undefined);
    }
  };

  // Mass deletion handlers
  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedTimeSlots(new Set());
  };

  const handleTimeSlotSelection = (timeSlotId: string, isSelected: boolean) => {
    const newSelection = new Set(selectedTimeSlots);
    if (isSelected) {
      newSelection.add(timeSlotId);
    } else {
      newSelection.delete(timeSlotId);
    }
    setSelectedTimeSlots(newSelection);
  };

  const handleMassDelete = () => {
    if (selectedTimeSlots.size === 0) {
      toast.error(t('schedule.noSlotsSelected'));
      return;
    }
    setShowMassDeleteDialog(true);
  };

  const confirmMassDelete = async () => {
    try {
      const deletePromises = Array.from(selectedTimeSlots).map(id => deleteTimeSlot(id));
      await Promise.all(deletePromises);
      
      toast.success(t('schedule.massDeleteSuccess', { count: selectedTimeSlots.size }));
      setSelectedTimeSlots(new Set());
      setIsSelectionMode(false);
      setShowMassDeleteDialog(false);
    } catch (error) {
      console.error('Error during mass deletion:', error);
      toast.error(t('schedule.massDeleteError'));
    }
  };

  const selectAllTimeSlots = () => {
    const visibleSlots = hasActiveFilters ? filteredTimeSlots : timeSlots;
    const allIds = new Set(visibleSlots.map(slot => slot.id));
    setSelectedTimeSlots(allIds);
  };

  const clearSelection = () => {
    setSelectedTimeSlots(new Set());
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="py-4 md:py-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-lg">{t('common.loading')}</div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="py-4 md:py-6 space-y-4 md:space-y-6">
        <div className="flex flex-col gap-3 md:gap-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl md:text-3xl font-bold">
              {t('schedule.title')}
            </h1>
          </div>
          
          {canManageSchedule && (
            <div className="w-full">
              {!isSelectionMode ? (
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full">
                  <Button
                    onClick={toggleSelectionMode}
                    variant="outline"
                    className="flex items-center justify-center gap-1.5 md:gap-2 text-xs md:text-sm w-full sm:w-auto"
                    size="sm"
                  >
                    <CheckSquare className="h-4 w-4 md:h-5 md:w-5" />
                    {t('schedule.selectMultiple')}
                  </Button>
                  <Button
                    onClick={() => setIsCreateFormOpen(true)}
                    className="flex items-center justify-center gap-1.5 md:gap-2 text-xs md:text-sm w-full sm:w-auto"
                    size="sm"
                  >
                    <Plus className="h-4 w-4 md:h-5 md:w-5" />
                    {t('schedule.createTimeSlot')}
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex flex-col sm:flex-row gap-2 w-full">
                    <Button
                      onClick={selectAllTimeSlots}
                      variant="outline"
                      size="sm"
                      className="text-xs md:text-sm w-full sm:w-auto"
                    >
                      {t('schedule.selectAll')}
                    </Button>
                    <Button
                      onClick={clearSelection}
                      variant="outline"
                      size="sm"
                      className="text-xs md:text-sm w-full sm:w-auto"
                    >
                      {t('schedule.clearSelection')}
                    </Button>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 w-full">
                    <Button
                      onClick={handleMassDelete}
                      variant="destructive"
                      size="sm"
                      disabled={selectedTimeSlots.size === 0}
                      className="flex items-center justify-center gap-1.5 text-xs md:text-sm w-full sm:w-auto"
                    >
                      <Trash2 className="h-4 w-4" />
                      {t('schedule.deleteSelected')} ({selectedTimeSlots.size})
                    </Button>
                    <Button
                      onClick={toggleSelectionMode}
                      variant="ghost"
                      size="sm"
                      className="flex items-center justify-center gap-1.5 text-xs md:text-sm w-full sm:w-auto"
                    >
                      <X className="h-4 w-4" />
                      {t('common.cancel')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {!canManageSchedule && (
          <div className="mb-4 p-3 md:p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800 text-xs md:text-sm">
              You can view the schedule but don't have permission to manage it. Only admins and managers can create, edit, or delete time slots.
            </p>
          </div>
        )}

        {/* Selection Mode Banner */}
        {isSelectionMode && (
          <div className="mb-4 p-3 md:p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 text-xs md:text-sm">
              {t('schedule.selectionModeActive')} {selectedTimeSlots.size > 0 && t('schedule.selectedCount', { count: selectedTimeSlots.size })}
            </p>
          </div>
        )}

        {/* Schedule Filters */}
        <ScheduleFilters
          selectedDay={selectedDay}
          selectedWorkoutType={selectedWorkoutType}
          workoutTypes={workoutTypes}
          onDayChange={setSelectedDay}
          onWorkoutTypeChange={setSelectedWorkoutType}
          onClearFilters={clearFilters}
        />

        {/* Time Slots Grid */}
        <TimeSlotGrid
          timeSlots={hasActiveFilters ? filteredTimeSlots : timeSlots}
          onEditTimeSlot={handleEditTimeSlot}
          onDeleteTimeSlot={handleDeleteTimeSlot}
          onCreateTimeSlot={handleCreateTimeSlotForDay}
          canManage={canManageSchedule}
          filteredView={hasActiveFilters}
          isSelectionMode={isSelectionMode}
          selectedTimeSlots={selectedTimeSlots}
          onTimeSlotSelection={handleTimeSlotSelection}
        />

        {hasActiveFilters && filteredTimeSlots.length === 0 && (
          <div className="text-center text-muted-foreground py-12">
            <p className="text-lg mb-2">{t('schedule.noTimeSlots')}</p>
            <p className="text-sm">{t('common.tryAdjustingFilters')}</p>
          </div>
        )}
      </div>

      {/* Create Time Slot Form */}
      {canManageSchedule && (
        <TimeSlotCreateForm
          open={isCreateFormOpen}
          onOpenChange={handleCreateFormClose}
          onCreateTimeSlots={handleCreateTimeSlots}
          existingTimeSlots={timeSlots}
          preselectedDay={preselectedDay}
        />
      )}

      {/* Edit Time Slot Form */}
      {canManageSchedule && (
        <TimeSlotEditForm
          open={isEditFormOpen}
          onOpenChange={setIsEditFormOpen}
          timeSlot={editingTimeSlot}
          onUpdateTimeSlot={handleUpdateTimeSlot}
          existingTimeSlots={timeSlots}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <TimeSlotDeleteDialog
        timeSlot={deleteDialog}
        open={deleteDialog !== null}
        onOpenChange={() => setDeleteDialog(null)}
        onConfirm={confirmDeleteTimeSlot}
      />

      {/* Mass Delete Confirmation Dialog */}
      <TimeSlotMassDeleteDialog
        open={showMassDeleteDialog}
        onOpenChange={setShowMassDeleteDialog}
        onConfirm={confirmMassDelete}
        selectedCount={selectedTimeSlots.size}
      />
    </MainLayout>
  );
};

export default SchedulePage;
